import type { SchedulingStepFormData } from '../../schemas/schedulingStepFormSchema';
import type { CreateEpisodeStepFormData } from '../../schemas/createEpisodeStepFormSchema';

// Language options for Kinnser dropdown
export const LANGUAGE_OPTIONS = [
  { value: '1', label: 'English' },
  { value: '2', label: 'Spanish' },
  { value: '3', label: 'Chinese' },
  { value: '4', label: 'Russian' },
  { value: '5', label: 'Vietnamese' },
  { value: '6', label: 'Other/Unknown' },
];

// Admission type options for Kinnser dropdown
export const ADMISSION_TYPE_OPTIONS = [
  { value: '1', label: 'Emergency' },
  { value: '2', label: 'Urgent' },
  { value: '3', label: 'Elective' },
  { value: '4', label: 'Newborn' },
  { value: '5', label: 'Trauma' },
  { value: '6', label: 'Information not Available' },
];

// UB-04 Point of Origin codes (FL-15) for the WellSky ListPatientSourceKey
// select, with the intake form schema's labels so the push tab and the intake
// screen read identically.
//
// The intake vocabulary also has D, E and F. They are deliberately absent
// here: WellSky's home-health select has no option that represents them, so
// Roger Doc rejects them outright. Offering them disabled would be a
// permanent dead choice, so the form omits them and shows a notice instead
// when the intake captured one. Mirrors ROGERDOC_ADMISSION_SOURCE_CODES in
// backend/app/services/kinnser/service.py.
export const KINNSER_ADMISSION_SOURCE_OPTIONS = [
  {
    value: '1',
    label: '1 - Non-Health Care Facility (Physician Referral, Home)',
  },
  { value: '2', label: "2 - Clinic or Physician's Office" },
  { value: '4', label: '4 - Transfer from a Hospital (Different Facility)' },
  { value: '5', label: '5 - Transfer from SNF or ICF' },
  { value: '6', label: '6 - Transfer from another Health Care Facility' },
  { value: '8', label: '8 - Court/Law Enforcement' },
  { value: '9', label: '9 - Information Not Available' },
];

// The sendable codes as a set, for membership tests. Derived from the options
// above so the dropdown and every "can WellSky take this?" check cannot drift.
export const KINNSER_ADMISSION_SOURCE_VALUES = new Set(
  KINNSER_ADMISSION_SOURCE_OPTIONS.map(option => option.value)
);

// Labels for the UB-04 codes WellSky cannot represent, so the push tab's
// notice can name what the intake captured.
//
// This map is NOT the definition of "unsupported". Intake's
// discharge_facility_type is free text in practice -- real records hold
// things like "Short-stay Acute Hospital (IPPS)" and "NA - Not Discharged
// from an Inpatient Facility", which outnumber D/E/F by roughly fifty to one
// in production data. Anything outside KINNSER_ADMISSION_SOURCE_VALUES is
// unsendable; this map only supplies a nicer label for the three codes that
// happen to have one.
export const UNSUPPORTED_ADMISSION_SOURCE_LABELS: Record<string, string> = {
  D: 'D - Transfer within Hospital (Different Unit)',
  E: 'E - Transfer from Ambulatory Surgery Center',
  F: 'F - Transfer from Hospice Facility',
};

// Every UB-04 Point of Origin code the chart can store, for the Admission tab's
// editable source field. That screen writes straight to the admission row and
// never pushes to WellSky, so unlike the intake form it can offer D, E and F.
//
// Composed from the two constants above rather than restated, so there is no
// third copy of the code list to drift: the sendable seven keep the labels the
// intake and push tabs already show, and the three WellSky cannot represent
// reuse the labels the push tab's notice names them with. The backend rejects
// anything outside its own enum, so a drifted option here fails loudly rather
// than saving a code the claim cannot carry.
export const ADMISSION_SOURCE_OPTIONS = [
  ...KINNSER_ADMISSION_SOURCE_OPTIONS,
  ...Object.entries(UNSUPPORTED_ADMISSION_SOURCE_LABELS).map(
    ([value, label]) => ({ value, label })
  ),
];

// Map from ADMISSION_TYPE_OPTIONS label to value
export const ADMISSION_TYPE_VALUE_BY_LABEL: Record<string, string> =
  ADMISSION_TYPE_OPTIONS.reduce(
    (acc, option) => {
      acc[option.label] = option.value;
      return acc;
    },
    {} as Record<string, string>
  );

// HCPCS code options for Kinnser dropdown
export const HCPCS_CODE_OPTIONS = [
  { value: '25', label: "(Q5001) Care provided in patient's home/residence" },
  { value: '26', label: '(Q5002) Care provided in assisted living facility' },
  {
    value: '27',
    label: '(Q5009) Care provided in place not otherwise specified (NOS)',
  },
];

// Language key for "Other/Unknown"
export const OTHER_LANGUAGE_KEY = '6';

// Default values for scheduling step form
export const SCHEDULING_STEP_DEFAULT_VALUES: SchedulingStepFormData = {
  kinnser_create_episode: null,
  roger_create_episode: null,
  push_to_kinnser: null,
  push_to_roger: null,
  kinnser_visit_type: null,
  kinnser_creation_type: null,
  roc_date: null,
  roc_type: null,
  recert_date: null,
  kinnser_mrn: null,
  kinnser_internal_referral_id: null,
  kinnser_external_referral_id: null,
  kinnser_external_referral_name: null,
  kinnser_admission_source: null,
};

// Default values for create episode step form
export const CREATE_EPISODE_STEP_DEFAULT_VALUES: CreateEpisodeStepFormData = {
  // Push to EMR
  push_to_roger: null,
  push_to_kinnser: null,
  has_transferred_from_another_hha: null,
  prior_hha: null,
  f2f_encounter_date: null,
  // Clinic selection
  agency_clinic_id: null,
  // Kinnser Information
  kinnser_physician_id: null,
  kinnser_branch_id: null,
  kinnser_insurance_provider_id: null,
  kinnser_internal_referral_id: null,
  kinnser_external_referral_id: null,
  kinnser_external_referral_name: null,
  kinnser_admission_type: null,
  kinnser_admission_source: null,
  kinnser_hcpcs_code_key: null,
  kinnser_mrn: null,
  kinnser_hh_cahps_opt_out: null,
  kinnser_language_key: null,
  kinnser_other_language: null,
  // Roger physician (ClinicPhysician — episode.clinic_physician_id / intake.clinic_physician_id)
  clinic_physician_id: null,
  // Internal referral source (intake.internal_referral_source_id), RogerEMR only
  internal_referral_source_id: null,
  // Patient Identification
  patient_first_name: null,
  patient_middle_name: null,
  patient_last_name: null,
  patient_suffix: null,
  patient_dob: null,
  patient_gender: null,
  patient_ssn: null,
  patient_primary_language: null,
  // Contact Information
  patient_phone: null,
  patient_secondary_phone: null,
  patient_email: null,
  patient_street_address: null,
  patient_city: null,
  patient_state: null,
  patient_zip_code: null,
  patient_county: null,
  // Emergency contact (optional; pushed to WellSky when create/onboard)
  emergency_contact_name: null,
  emergency_contact_relationship: null,
  emergency_contact_phone: null,
  emergency_contact_street_address: null,
  emergency_contact_city: null,
  emergency_contact_state: null,
  emergency_contact_zip_code: null,
  // Insurance Information
  medicaid_id: null,
  // Notes
  patient_notes: null,
};
